import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Home, Building2, Coins } from "lucide-react";
import api from "@/lib/api";
import { PageShell } from "@/components/site/PageShell";
import { Hero } from "@/components/landing/Hero";
import { QualityPillars } from "@/components/landing/QualityPillars";
import { LocationMap } from "@/components/landing/LocationMap";
import { Reveal } from "@/hooks/useReveal";
import { SeoHead } from "@/components/SeoHead";
import { DEFAULT_SEO, resolveSeo } from "@/data/apartmentSeo";
import { useT, useLang } from "@/i18n/LanguageProvider";
import { APARTMENTS } from "@/data/apartments";
import { ConstructionTracker } from "@/components/landing/ConstructionTracker";

/**
 * HomePage – slim entry page for the redesigned multi-page site.
 * Surfaces the brand, the four quality pillars, the live construction
 * status and three sectional teasers (Apartments, Investment, Contact).
 */
export default function HomePage() {
    const t = useT();
    const { lang } = useLang();
    const [availability, setAvailability] = useState({});
    const seo = resolveSeo(DEFAULT_SEO, lang);

    useEffect(() => {
        api.get("/apartments/availability")
            .then((r) => {
                const map = {};
                (r.data || []).forEach((a) => { map[a.id] = a.status; });
                setAvailability(map);
            })
            .catch(() => {});
    }, []);

    const TEASERS = [
        {
            icon: Home,
            eyebrow: t({ de: "Wohnungen", en: "Apartments" }),
            title: t({ de: "Vier Wohnungen mit Charakter", en: "Four homes with character" }),
            text: t({
                de: "EG · OG · Penthouse · Loft – jede Wohnung individuell geschnitten, mit Carport und Kellerraum.",
                en: "Ground · Upper · Penthouse · Loft – each apartment with carport and storage.",
            }),
            to: "/wohnungen",
            cta: t({ de: "Wohnungen entdecken", en: "Explore apartments" }),
            tone: "mint",
        },
        {
            icon: Building2,
            eyebrow: t({ de: "Architektur & Energie", en: "Architecture & energy" }),
            title: t({ de: "Effizienzhaus 40 mit Charakter", en: "Energy class 40 with character" }),
            text: t({
                de: "Wärmepumpe je Wohnung, 2 kW PV inkl. Speicher separat für jede Wohnung, Wallbox optional. Klassische Dachform trifft modernes Detail.",
                en: "Heat pump per apartment, 2 kW PV with dedicated battery storage per apartment, wallbox optional. Classic roof meets modern detail.",
            }),
            to: "/architektur",
            cta: t({ de: "Architektur ansehen", en: "View architecture" }),
            tone: "sand",
        },
        {
            icon: Coins,
            eyebrow: t({ de: "Kapitalanlage", en: "Investment" }),
            title: t({ de: "5 % AfA + Mietrendite", en: "5 % depreciation + rental yield" }),
            text: t({
                de: "Finanzierungs- und Mietrendite-Rechner, persönlicher Steuereffekt, Vermögensaufbau-Plan per E-Mail.",
                en: "Mortgage and yield calculators, personal tax effect, wealth plan by email.",
            }),
            to: "/investor",
            cta: t({ de: "Zur Kapitalanlage", en: "To investment" }),
            tone: "terracotta",
        },
    ];

    return (
        <PageShell testId="home-page">
            <SeoHead seo={seo} />
            <Hero />

            <Reveal><QualityPillars /></Reveal>

            {/* Teaser triplet */}
            <section className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-24 sm:py-32" data-testid="home-teasers">
                <div className="grid lg:grid-cols-12 gap-10 mb-14">
                    <div className="lg:col-span-5">
                        <span className="font-body text-xs uppercase tracking-[0.22em] font-semibold text-terracotta-500">
                            {t({ de: "Übersicht", en: "Overview" })}
                        </span>
                        <h2 className="font-display font-black tracking-tight text-sage-500 text-3xl sm:text-4xl lg:text-5xl mt-4 leading-tight">
                            {t({ de: "Drei Wege durch", en: "Three ways through" })}
                            <span className="text-terracotta-400"> G21.</span>
                        </h2>
                    </div>
                    <div className="lg:col-span-6 lg:col-start-7 self-end">
                        <p className="font-body text-lg text-sage-500/75 leading-relaxed">
                            {t({
                                de: "Wählen Sie die Perspektive, die zu Ihnen passt – Eigenheim, Architektur oder Kapitalanlage.",
                                en: "Pick the perspective that suits you – home, architecture, or investment.",
                            })}
                        </p>
                    </div>
                </div>

                <div className="grid md:grid-cols-3 gap-6" data-testid="teaser-grid">
                    {TEASERS.map((teaser, i) => {
                        const Icon = teaser.icon;
                        const surface = {
                            mint: "bg-mint-100",
                            sand: "bg-sand-100",
                            terracotta: "bg-terracotta-50",
                        }[teaser.tone];
                        return (
                            <Reveal key={i} delay={i * 110} className="h-full">
                                <Link
                                    to={teaser.to}
                                    className={`group relative h-full flex flex-col ${surface} rounded-3xl p-8 lg:p-10 transition-all hover:-translate-y-1 hover:shadow-[0_16px_40px_rgba(61,90,75,0.10)]`}
                                    data-testid={`home-teaser-${i}`}
                                >
                                    <div className="w-12 h-12 flex items-center justify-center rounded-full bg-white text-sage-500 mb-6 group-hover:bg-sage-500 group-hover:text-white transition-colors">
                                        <Icon size={20} strokeWidth={1.7} />
                                    </div>
                                    <span className="font-body text-xs uppercase tracking-[0.22em] font-semibold text-sage-500/60">
                                        {teaser.eyebrow}
                                    </span>
                                    <h3 className="font-display font-bold text-2xl text-sage-500 mt-2 leading-snug">
                                        {teaser.title}
                                    </h3>
                                    <p className="font-body text-sm text-sage-500/75 mt-4 leading-relaxed">
                                        {teaser.text}
                                    </p>
                                    <span className="inline-flex items-center gap-2 mt-auto pt-6 text-sage-500 font-body font-medium text-sm group-hover:gap-3 transition-all">
                                        {teaser.cta}
                                        <ArrowRight size={16} />
                                    </span>
                                </Link>
                            </Reveal>
                        );
                    })}
                </div>
            </section>

            {/* Apartment quick-pick */}
            <section className="bg-white py-24 sm:py-32 border-y border-black/5" data-testid="home-apartments-quickpick">
                <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
                    <div className="grid lg:grid-cols-12 gap-10 mb-14">
                        <div className="lg:col-span-7">
                            <span className="font-body text-xs uppercase tracking-[0.22em] font-semibold text-terracotta-500">
                                {t({ de: "Vier Eigentumswohnungen", en: "Four condominiums" })}
                            </span>
                            <h2 className="font-display font-black tracking-tight text-sage-500 text-3xl sm:text-4xl lg:text-5xl mt-4 leading-tight text-balance">
                                {t({ de: "Welche passt", en: "Which one suits" })}
                                <span className="text-terracotta-400"> {t({ de: "zu Ihnen?", en: "you?" })}</span>
                            </h2>
                        </div>
                        <div className="lg:col-span-4 lg:col-start-9 self-end">
                            <Link
                                to="/wohnungen"
                                className="inline-flex items-center gap-2 text-sage-500 font-body font-medium hover:gap-3 transition-all"
                            >
                                {t({ de: "Alle 4 Wohnungen im Detail", en: "View all 4 apartments" })}
                                <ArrowRight size={16} />
                            </Link>
                        </div>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6">
                        {APARTMENTS.map((apt, i) => {
                            const status = availability[apt.id] || "verfuegbar";
                            return (
                                <Reveal key={apt.id} delay={i * 90} className="h-full">
                                    <Link
                                        to={`/wohnungen#${apt.id}`}
                                        className="group block h-full rounded-2xl overflow-hidden bg-mint-50 hover:shadow-[0_12px_30px_rgba(61,90,75,0.08)] transition-shadow"
                                        data-testid={`home-apt-${apt.id}`}
                                    >
                                        <div className="aspect-[4/5] overflow-hidden">
                                            <img
                                                src={apt.image}
                                                alt={apt.name}
                                                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                                            />
                                        </div>
                                        <div className="p-4 sm:p-5">
                                            <div className="flex items-center justify-between">
                                                <span className="font-body text-[10px] uppercase tracking-[0.2em] text-sage-500/60">
                                                    {apt.shortName}
                                                </span>
                                                <span
                                                    className={`inline-block w-2 h-2 rounded-full ${
                                                        status === "verfuegbar"
                                                            ? "bg-mint-400"
                                                            : status === "reserviert"
                                                            ? "bg-terracotta-300"
                                                            : "bg-sage-500/40"
                                                    }`}
                                                />
                                            </div>
                                            <h3 className="font-display font-bold text-sage-500 text-base sm:text-lg mt-1 leading-snug">
                                                {apt.name}
                                            </h3>
                                            <div className="mt-1 font-body text-xs text-sage-500/60">
                                                {apt.size} m² · {apt.rooms} Zi.
                                            </div>
                                        </div>
                                    </Link>
                                </Reveal>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* Construction tracker */}
            <Reveal><ConstructionTracker /></Reveal>

            {/* CTA strip */}
            <section className="py-24 sm:py-32" data-testid="home-cta-strip">
                <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
                    <div className="bg-sage-500 rounded-[2rem] sm:rounded-[3rem] p-10 sm:p-16 lg:p-20 relative overflow-hidden">
                        <div className="absolute -right-20 -top-20 w-80 h-80 rounded-full bg-mint-200/15 pointer-events-none" />
                        <div className="absolute -right-10 bottom-0 w-56 h-56 rounded-full bg-terracotta-300/15 pointer-events-none" />
                        <div className="relative grid md:grid-cols-12 gap-8 items-center">
                            <div className="md:col-span-7">
                                <span className="font-body text-xs uppercase tracking-[0.22em] font-semibold text-terracotta-300">
                                    {t({ de: "Lust auf G21?", en: "Interested in G21?" })}
                                </span>
                                <h2 className="font-display font-black tracking-tight text-white text-3xl sm:text-4xl lg:text-5xl mt-4 leading-tight text-balance">
                                    {t({ de: "Lassen Sie uns über Ihre", en: "Let us talk about your" })}
                                    <span className="text-terracotta-300"> {t({ de: "Wunschwohnung sprechen.", en: "dream apartment." })}</span>
                                </h2>
                                <p className="font-body text-white/75 mt-5 max-w-lg leading-relaxed">
                                    {t({
                                        de: "Persönlich, regional, ohne Vermittlerprovision. Wir antworten in der Regel innerhalb von 24 Stunden.",
                                        en: "Personal, regional, no broker fee. We typically reply within 24 hours.",
                                    })}
                                </p>
                            </div>
                            <div className="md:col-span-5 flex flex-col gap-3">
                                <Link
                                    to="/kontakt"
                                    className="inline-flex items-center justify-center gap-2 px-7 py-4 rounded-full bg-white text-sage-500 font-body font-medium text-base hover:bg-mint-50 transition-colors"
                                    data-testid="home-cta-contact"
                                >
                                    {t({ de: "Kontakt aufnehmen", en: "Get in touch" })}
                                    <ArrowRight size={16} />
                                </Link>
                                <Link
                                    to="/wohnungen"
                                    className="inline-flex items-center justify-center gap-2 px-7 py-4 rounded-full border border-white/40 text-white font-body font-medium text-base hover:bg-white/10 transition-colors"
                                    data-testid="home-cta-apartments"
                                >
                                    <Home size={16} />
                                    {t({ de: "Wohnungen ansehen", en: "View apartments" })}
                                </Link>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Location section with interactive map */}
            <Reveal><LocationMap /></Reveal>
        </PageShell>
    );
}
